import * as React from 'react';
import classNames from 'classnames';

import { faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Portal from '../../atoms/Portal';

import styles from './Lightbox.module.scss';
import { ColorStyleProps } from '../../../@types/theme';
import { Button } from '../../atoms';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onSubmit'> {
  id: string;
  show: boolean;
  onHide?: () => void;
  title?: string;
  closeButton?: boolean;
  backdrop?: boolean;
  children?: React.ReactNode;
  isScrollable?: boolean;
  backgroundColor?: ColorStyleProps;
  /**
   * When true, the lightbox will adjust its size to fit the content (usually an image)
   * instead of having a fixed size. This is ignored when isScrollable is true.
   */
  fitContent?: boolean;
}

const PORTAL_ID = 'default-lightbox-portal';

export const Lightbox: React.FunctionComponent<Props> = ({
  id,
  className,
  style,
  show = false,
  closeButton = true,
  onHide = () => null,
  backdrop = true,
  children,
  isScrollable = false,
  backgroundColor = 'grayLight3',
  fitContent = false,
  ...htmlElementProps
}: Props) => {
  const lightboxRef = React.useRef<HTMLDivElement | null>(null);

  const closeLightbox: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (backdrop && lightboxRef.current === e.target) {
      onHide();
    }
  };

  // Close modal with escape key
  React.useEffect(() => {
    const closeOnEscapeKey = (e: KeyboardEvent) =>
      e.key === 'Escape' ? onHide() : null;
    document.body.addEventListener('keydown', closeOnEscapeKey);
    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKey);
    };
  }, [onHide]);

  /**
   * Hide body scroll if modal is open
   */
  React.useEffect(() => {
    if (show) {
      document.body.style.overflowY = 'hidden';
    }
    return () => {
      if (
        document.getElementById(PORTAL_ID)?.childElementCount ===
        (0 || undefined)
      ) {
        document.body.style.overflowY = 'auto';
      }
    };
  }, [show]);

  if (!show) return null;
  return (
    <Portal wrapperId={PORTAL_ID}>
      <div
        id={id}
        className={classNames(styles.background, {
          scrollable: isScrollable,
        })}
        onClick={closeLightbox}
        role="presentation"
        ref={lightboxRef}
      >
        <div
          className={classNames(styles.wrapper, className, {
            scrollable: isScrollable,
            'fit-content': fitContent && !isScrollable,
            [`bg-${backgroundColor}`]: backgroundColor,
          })}
          style={{
            ...style,
            ...(fitContent && !isScrollable
              ? { width: 'auto', maxWidth: '90%' }
              : {}),
          }}
          role="dialog"
          aria-modal
          {...htmlElementProps}
        >
          {closeButton && (
            <Button
              className={styles['close-button']}
              variant="link"
              onClick={() => onHide()}
              aria-label="close button"
            >
              <FontAwesomeIcon icon={faXmarkLarge} />
            </Button>
          )}
          <div className={styles.content}>{children}</div>
        </div>
      </div>
    </Portal>
  );
};

export default Lightbox;
